import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LuShield, LuTriangleAlert, LuUsers } from 'react-icons/lu';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { validateUsername, isUsernameAvailable } from '../firebase/Username';
import { COPY } from '../contracts/copyMap';
import { ROUTES } from '../contracts/routes';
import {
  AGE_MIN,
  AGE_MAX,
  AGE_DEFAULT,
  GENDER_OPTIONS,
  ONBOARDING_STEPS,
  ONBOARDING_STEP_COUNT,
} from '../contracts/onboarding';
import OnboardingShell from '../components/onboarding/OnboardingShell';
import LegalModal from '../components/legal/LegalModal';

// Mandatory 4-step onboarding wizard. AuthGate routes any authenticated user
// without onboarding_complete here. State lives in this orchestrator; the four
// steps below are presentational. The whole thing persists in a single
// updateUserData() call at the end (abandon == restart, no partial state).
//
//   age ──> gender ──> handle ──> terms ──> updateUserData ──> from || /create
//
// Handle availability is debounced live; a legacy user's existing username is
// pre-filled and treated as already-theirs (no re-check, no re-reservation).

const HANDLE_DEBOUNCE_MS = 400;

// ── Step 1: age slider ──────────────────────────────────────────────
const AgeStep = ({ age, onChange }) => (
  <div>
    <div className="flex items-end justify-center gap-2">
      <span
        className="font-display text-bone leading-none"
        style={{ fontSize: '96px' }}
        data-testid="age-value"
      >
        {age}
      </span>
      <span className="mb-4 font-display italic text-bone-dim">
        {COPY.ONBOARDING_AGE_UNIT}
      </span>
    </div>
    <input
      type="range"
      min={AGE_MIN}
      max={AGE_MAX}
      value={age}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={COPY.ONBOARDING_AGE_HEADLINE}
      className="mt-6 w-full appearance-none bg-transparent onboarding-range"
    />
    <div className="mt-2 flex justify-between font-display text-[12px] text-bone-dim">
      <span>{AGE_MIN}</span>
      <span>{AGE_MAX}</span>
    </div>
  </div>
);

// ── Step 2: gender radio grid ───────────────────────────────────────
const GenderStep = ({ gender, onChange }) => (
  <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={COPY.ONBOARDING_GENDER_HEADLINE}>
    {GENDER_OPTIONS.map((opt) => {
      const selected = gender === opt.key;
      return (
        <button
          key={opt.key}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(opt.key)}
          className={[
            'flex items-center gap-3 rounded-field border px-4 py-4 text-left transition-all duration-200',
            selected
              ? 'border-aurum bg-aurum-faint'
              : 'border-hairline bg-card-sheen hover:border-bone-dim',
          ].join(' ')}
        >
          <span
            className={[
              'flex h-5 w-5 items-center justify-center rounded-full border',
              selected ? 'border-aurum' : 'border-bone-dim',
            ].join(' ')}
            aria-hidden="true"
          >
            {selected && <span className="h-2.5 w-2.5 rounded-full bg-aurum" />}
          </span>
          <span className="font-display text-bone">{opt.label}</span>
        </button>
      );
    })}
  </div>
);

// ── Step 3: handle ──────────────────────────────────────────────────
const HandleStep = ({ handle, onChange, status, message }) => (
  <div>
    <div className="flex items-center gap-2 rounded-field border border-hairline bg-[#1B1B1A] px-4 h-[58px] focus-within:border-aurum transition-colors">
      <span className="font-display text-bone-dim text-lg">@</span>
      <input
        type="text"
        value={handle}
        onChange={(e) => onChange(e.target.value.trim())}
        placeholder={COPY.ONBOARDING_HANDLE_PLACEHOLDER}
        aria-label={COPY.ONBOARDING_HANDLE_HEADLINE}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck="false"
        maxLength={20}
        className="w-full bg-transparent font-display text-lg text-bone placeholder:text-vein-dim focus:outline-none"
      />
    </div>
    <div className="mt-3 flex items-center gap-2 font-display text-[13px]">
      <LuUsers className="text-aurum" aria-hidden="true" />
      {handle && status === 'available' ? (
        <span className="text-bone-dim">
          {COPY.ONBOARDING_HANDLE_APPEARS_PREFIX}{' '}
          <span className="text-aurum">@{handle}</span>{' '}
          {COPY.ONBOARDING_HANDLE_APPEARS_SUFFIX}
        </span>
      ) : (
        <span
          className={
            status === 'taken' || status === 'invalid' || status === 'error'
              ? 'text-[#E0A4A4]'
              : 'text-bone-dim'
          }
          data-testid="handle-status"
        >
          {message}
        </span>
      )}
    </div>
  </div>
);

// ── Step 4: terms ───────────────────────────────────────────────────
const TermsStep = ({ agreed, onChange, onOpenLegal }) => (
  <div>
    <div className="rounded-field border border-hairline bg-card-sheen p-5 space-y-4">
      <div className="flex items-start gap-3">
        <LuShield className="mt-0.5 shrink-0 text-aurum" aria-hidden="true" />
        <p className="m-0 font-display text-bone text-[15px]">
          {COPY.ONBOARDING_TERMS_PRIVACY_NOTE}
        </p>
      </div>
      <div className="flex items-start gap-3">
        <LuTriangleAlert className="mt-0.5 shrink-0 text-aurum" aria-hidden="true" />
        <p className="m-0 font-display text-bone text-[15px]">
          {COPY.ONBOARDING_TERMS_MEDICAL_NOTE}
        </p>
      </div>
    </div>
    <label className="mt-5 flex items-center gap-3 cursor-pointer">
      <button
        type="button"
        role="checkbox"
        aria-checked={agreed}
        onClick={() => onChange(!agreed)}
        className={[
          'flex h-6 w-6 items-center justify-center rounded-md border transition-all',
          agreed ? 'border-aurum bg-aurum text-obsidian' : 'border-bone-dim bg-transparent',
        ].join(' ')}
      >
        {agreed && <span aria-hidden="true">&#10003;</span>}
      </button>
      <span className="font-display text-bone text-[15px]">
        {COPY.ONBOARDING_TERMS_AGREE_PREFIX}{' '}
        <button
          type="button"
          onClick={() => onOpenLegal('tos')}
          className="text-aurum underline"
        >
          {COPY.ONBOARDING_TERMS_LINK_TOS}
        </button>{' '}
        {COPY.ONBOARDING_TERMS_CONJUNCTION}{' '}
        <button
          type="button"
          onClick={() => onOpenLegal('privacy')}
          className="text-aurum underline"
        >
          {COPY.ONBOARDING_TERMS_LINK_PRIVACY}
        </button>
        .
      </span>
    </label>
  </div>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { userData, updateUserData } = useUser();

  const existingUsername = userData?.username || '';

  const [stepIndex, setStepIndex] = useState(0);
  const [age, setAge] = useState(AGE_DEFAULT);
  const [gender, setGender] = useState(null);
  const [handle, setHandle] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null); // 'tos' | 'privacy' | null

  // handle availability: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [handleStatus, setHandleStatus] = useState('idle');
  const [handleMessage, setHandleMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill an existing username (legacy users) once the doc resolves.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!prefilledRef.current && existingUsername) {
      prefilledRef.current = true;
      setHandle(existingUsername);
      setHandleStatus('available');
    }
  }, [existingUsername]);

  // Debounced handle availability check. The user's own existing handle is
  // already reserved to them — accept it without a network round-trip.
  useEffect(() => {
    if (!handle) {
      setHandleStatus('idle');
      setHandleMessage('');
      return undefined;
    }
    if (handle === existingUsername) {
      setHandleStatus('available');
      setHandleMessage('');
      return undefined;
    }
    const formatError = validateUsername(handle);
    if (formatError) {
      setHandleStatus('invalid');
      setHandleMessage(formatError);
      return undefined;
    }
    setHandleStatus('checking');
    setHandleMessage(COPY.ONBOARDING_HANDLE_CHECKING);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const available = await isUsernameAvailable(handle);
        if (cancelled) return;
        setHandleStatus(available ? 'available' : 'taken');
        setHandleMessage(available ? '' : COPY.ONBOARDING_HANDLE_TAKEN);
      } catch {
        // Couldn't reach the index — don't claim "taken" (misleading) and don't
        // claim available (they'd fail at submit). Block with an honest message.
        if (cancelled) return;
        setHandleStatus('error');
        setHandleMessage(COPY.ONBOARDING_HANDLE_CHECK_FAILED);
      }
    }, HANDLE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [handle, existingUsername]);

  const stepKey = ONBOARDING_STEPS[stepIndex];

  const stepValid =
    (stepKey === 'age' && age >= AGE_MIN && age <= AGE_MAX) ||
    (stepKey === 'gender' && gender !== null) ||
    (stepKey === 'handle' && handleStatus === 'available') ||
    (stepKey === 'terms' && agreed === true);

  const isLastStep = stepIndex === ONBOARDING_STEP_COUNT - 1;

  const handleBack = useCallback(() => {
    setError(null);
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    const now = new Date().toISOString();
    try {
      await updateUserData({
        age,
        gender,
        username: handle,
        age_attested: true,
        terms_accepted_at: now,
        onboarding_complete: true,
        onboarding_completed_at: now,
      });
      const dest = (location.state && location.state.from) || ROUTES.CREATE;
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err && err.message ? err.message : '';
      if (/username already taken/i.test(msg)) {
        // Lost a race against the live check — send them back to fix the handle.
        setHandleStatus('taken');
        setHandleMessage(COPY.ONBOARDING_HANDLE_TAKEN);
        setStepIndex(ONBOARDING_STEPS.indexOf('handle'));
        setError(COPY.ONBOARDING_HANDLE_TAKEN);
      } else {
        setError(COPY.ONBOARDING_GENERIC_ERROR);
      }
      setSubmitting(false);
    }
  }, [age, gender, handle, updateUserData, navigate, location.state]);

  const handleContinue = useCallback(() => {
    if (!stepValid) return;
    setError(null);
    if (isLastStep) {
      submit();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [stepValid, isLastStep, submit]);

  // Escape hatch — onboarding is mandatory but must never softlock. Signing
  // out drops auth, so AuthGate bounces to /sign-in on its own; no navigate
  // needed. Wizard state is intentionally discarded (abandon == restart).
  const handleExit = useCallback(async () => {
    setError(null);
    try {
      await logout();
    } catch {
      setError(COPY.ONBOARDING_GENERIC_ERROR);
    }
  }, [logout]);

  const headlineMap = {
    age: COPY.ONBOARDING_AGE_HEADLINE,
    gender: COPY.ONBOARDING_GENDER_HEADLINE,
    handle: COPY.ONBOARDING_HANDLE_HEADLINE,
    terms: COPY.ONBOARDING_TERMS_HEADLINE,
  };
  const subtitleMap = {
    age: COPY.ONBOARDING_AGE_SUBTITLE,
    gender: COPY.ONBOARDING_GENDER_SUBTITLE,
    handle: COPY.ONBOARDING_HANDLE_SUBTITLE,
    terms: COPY.ONBOARDING_TERMS_SUBTITLE,
  };

  return (
    <>
      <OnboardingShell
        stepIndex={stepIndex}
        stepCount={ONBOARDING_STEP_COUNT}
        headline={headlineMap[stepKey]}
        subtitle={subtitleMap[stepKey]}
        onBack={handleBack}
        onContinue={handleContinue}
        onExit={handleExit}
        continueLabel={isLastStep ? COPY.ONBOARDING_ENTER_CTA : COPY.ONBOARDING_CONTINUE_CTA}
        continueDisabled={!stepValid}
        submitting={submitting}
        error={error}
      >
        {stepKey === 'age' && <AgeStep age={age} onChange={setAge} />}
        {stepKey === 'gender' && <GenderStep gender={gender} onChange={setGender} />}
        {stepKey === 'handle' && (
          <HandleStep
            handle={handle}
            onChange={setHandle}
            status={handleStatus}
            message={handleMessage}
          />
        )}
        {stepKey === 'terms' && (
          <TermsStep agreed={agreed} onChange={setAgreed} onOpenLegal={setLegalDoc} />
        )}
      </OnboardingShell>
      <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />
    </>
  );
};

export default Onboarding;
